import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Loader2, Layers, SlidersHorizontal } from 'lucide-react';
import FilterPanel from '../components/FilterPanel';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import BrandLogo from '../components/BrandLogo';
import VehicleBanner from '../components/VehicleBanner';
import TagLinks from '../components/TagLinks';
import { PopularModelsBlock } from '../components/PopularModelsBlock';
import { RelatedGuidesBlock } from '../components/RelatedGuidesBlock';
import SortSelect, { sortProducts, isBestPrice, useTopRatedFilter, isRecentProduct } from '../components/SortSelect';
import { Button } from '../components/ui/button';
import { productsAPI, cartAPI, categoriesAPI, uploadsAPI } from '../services/api';
import { ga } from '../services/ga';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { ENERGIES, vehicleEnergy, productFuelOk, energyLabel } from '../services/fuel';
import { usePageMeta } from '../services/seo';
import { renderMarkdown } from '../services/markdown';
import FaqSection from '../components/FaqSection';
import Breadcrumb from '../components/Breadcrumb';
import { subcategoryPath, subcategoryFromSlug, parentOf, childrenOf, topSubcategories, nodeNames } from '../services/catalogPath';
import SubcategoryChips from '../components/SubcategoryChips';
import QuickCrossFilters from '../components/QuickCrossFilters';
import { useT } from '../services/i18n';
import { catName, subName, catSeo, subSeoOf, useTerms } from '../services/catalogI18n';
import { specTemplate, standardKey, attrValue } from '../services/specs';

const CategoryPage = () => {
  const { t, lang } = useT();
  const tt = useTerms(lang);
  const { slug, sub: subSlug, subsub: subsubSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const vehicle = useSelectedVehicle();
  const [showAll, setShowAll] = useState(false);
  const [products, setProducts] = useState([]);
  const [compatibleIds, setCompatibleIds] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = vehicleStore.toParams(vehicle);
      const [all, compatible] = await Promise.all([
        productsAPI.getAll(slug),
        params ? productsAPI.getAll(slug, null, params) : Promise.resolve(null),
      ]);
      setProducts(all);
      setCompatibleIds(compatible ? new Set(compatible.map(p => p.id)) : null);
      ga.viewItemList(slug, slug, all || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [slug, vehicle]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleAddToCart = async (productId) => {
    setAddingToCart(productId);
    try {
      await cartAPI.addItem(productId);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setAddingToCart(null);
    }
  };

  const readSort = (s) => localStorage.getItem(`catSort:${s}`) || 'relevance';
  const [sort, setSort] = useState(() => readSort(slug));
  const sortSlug = useRef(slug);
  useEffect(() => { if (sortSlug.current !== slug) return; if (sort === 'relevance') localStorage.removeItem(`catSort:${slug}`); else localStorage.setItem(`catSort:${slug}`, sort); }, [sort, slug]);
  useEffect(() => { sortSlug.current = slug; setSort(readSort(slug)); }, [slug]);
  const [bestOnly, setBestOnly] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [searchParams] = useSearchParams();
  const legacySous = searchParams.get('sous') || '';
  const isLegacyUrl = location.pathname.startsWith('/categorie/');
  const activeSub = subSlug ? subcategoryFromSlug(categoryInfo, subSlug, subsubSlug) : (isLegacyUrl ? legacySous : '');
  const activeParent = parentOf(categoryInfo, activeSub);
  const activeNodes = activeSub ? nodeNames(categoryInfo, activeSub) : [];
  const setActiveSub = (sub) => navigate(sub ? subcategoryPath(categoryInfo || { slug }, sub) : `/pieces/${slug}`, { replace: true });
  const subSeo = subSeoOf(categoryInfo, activeSub, lang);
  const seo = catSeo(categoryInfo, lang);

  useEffect(() => {
    categoriesAPI.getAll().then(list => setCategoryInfo(list.find(c => c.slug === slug) || null)).catch(() => {});
  }, [slug]);
  const getCategoryName = () => catName(categoryInfo, lang) || t('cat.products');
  const subLabel = (s) => subName(categoryInfo, s, lang);
  const isFr = lang === 'fr';
  const seoName = catName(categoryInfo, lang) || slug;
  const brandsLabel = [...new Set(products.map(p => p.brand))].slice(0, 5).join(', ') || 'Bosch, Febi, TRW';
  usePageMeta({
    rawTitle: activeSub ? (subSeo.seo_title || `${subLabel(activeSub)} — ${getCategoryName()} | OTOPIECES.COM`) : (seo.seo_title || undefined),
    title: !activeSub && !seo.seo_title ? getCategoryName() : undefined,
    keywords: isFr ? (categoryInfo?.tags || []) : [],
    description: activeSub
      ? (subSeo.meta_description || t('cat.metaSubFallback', { sub: subName(categoryInfo, activeSub, lang), cat: seoName.toLowerCase(), brands: brandsLabel }))
      : (seo.meta_description || seo.excerpt || t('cat.metaFallback', { cat: seoName.toLowerCase(), brands: brandsLabel })),
    image: (activeSub && categoryInfo?.subcategory_images?.[activeSub]) || categoryInfo?.image,
    path: activeSub ? subcategoryPath(categoryInfo || { slug }, activeSub) : `/pieces/${slug}`,
    noindex: !isLoading && !products.some(p => !activeSub || activeNodes.includes(p.subcategory) || (p.extra_categories || []).some(x => x.category === slug && activeNodes.includes(x.subcategory))),
  });
  const filtersKey = `catFilters:${slug}/${activeSub || ''}`;
  const readFilters = () => { try { return JSON.parse(localStorage.getItem(filtersKey)) || {}; } catch { return {}; } };
  const [brandFilter, setBrandFilter] = useState(() => readFilters().brand || []);
  const [priceInput, setPriceInput] = useState(() => readFilters().priceInput || { min: '', max: '' });
  const [priceFilter, setPriceFilter] = useState(() => readFilters().price || { min: null, max: null });
  const [attrFilter, setAttrFilter] = useState(() => readFilters().attr || {});
  const [fuelFilter, setFuelFilter] = useState(() => readFilters().fuel || '');
  const vehicleEnergyKey = vehicleEnergy(vehicle?.engine?.fuel);
  const [attrExpanded, setAttrExpanded] = useState({});
  const skipSave = useRef(false);
  const [loadTick, setLoadTick] = useState(0);
  const firstKey = useRef(filtersKey);
  useEffect(() => {
    if (firstKey.current === filtersKey) { firstKey.current = null; return; }
    const st = readFilters();
    skipSave.current = true;
    setBrandFilter(st.brand || []); setPriceInput(st.priceInput || { min: '', max: '' }); setPriceFilter(st.price || { min: null, max: null }); setAttrFilter(st.attr || {}); setFuelFilter(st.fuel || '');
    setLoadTick((t) => t + 1);
  }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (skipSave.current) { skipSave.current = false; return; }
    const empty = !brandFilter.length && priceFilter.min === null && priceFilter.max === null && !Object.values(attrFilter).some((v) => v.length) && !fuelFilter;
    if (empty) localStorage.removeItem(filtersKey);
    else localStorage.setItem(filtersKey, JSON.stringify({ brand: brandFilter, price: priceFilter, priceInput, attr: attrFilter, fuel: fuelFilter }));
  }, [filtersKey, brandFilter, priceFilter, priceInput, attrFilter, fuelFilter, loadTick]);

  const availableBrands = [...new Set(products.map(p => p.brand))].sort();

  const subsOf = (p) => [p.category === slug ? p.subcategory : null, ...(p.extra_categories || []).filter(x => x.category === slug).map(x => x.subcategory)].filter(Boolean);
  const inSub = (p, sub) => subsOf(p).some(s => nodeNames(categoryInfo, sub).includes(s));

  const baseProducts = (compatibleIds && !showAll
    ? products.filter(p => compatibleIds.has(p.id))
    : products).filter(p => !activeSub || inSub(p, activeSub));

  const countBySub = products.reduce((acc, p) => { subsOf(p).forEach(s => { acc[s] = (acc[s] || 0) + 1; const par = parentOf(categoryInfo, s); if (par) acc[par] = (acc[par] || 0) + 1; }); return acc; }, {});

  const preFilter = baseProducts.filter(p =>
    (brandFilter.length === 0 || brandFilter.includes(p.brand)) &&
    (priceFilter.min === null || p.price >= priceFilter.min) &&
    (priceFilter.max === null || p.price <= priceFilter.max) &&
    productFuelOk(p, fuelFilter) &&
    Object.entries(attrFilter).every(([label, values]) => !values.length || values.includes(String(attrValue(p, label) ?? '')))
  );
  const fuelCounts = ENERGIES.reduce((acc, e) => { acc[e] = baseProducts.filter(p => productFuelOk(p, e)).length; return acc; }, {});
  const bestCount = preFilter.filter(isBestPrice).length;
  const topRatedFilter = useTopRatedFilter(bestOnly ? preFilter.filter(isBestPrice) : preFilter);
  const visibleProducts = sortProducts(topRatedFilter.list, sort, fuelFilter || vehicleEnergyKey);

  // Technical attribute facets: template labels (admin spec_fields) or, failing that, the products' own attributes
  const specFacets = (() => {
    const template = specTemplate(categoryInfo, activeSub);
    const labels = template ? template.filter(l => !standardKey(l)) : [...new Set(baseProducts.flatMap(p => Object.keys(p.attributes || {})))];
    return labels.map(label => {
      const others = baseProducts.filter(p => Object.entries(attrFilter).every(([l, vs]) => l === label || !vs.length || vs.includes(String(attrValue(p, l) ?? ''))));
      const counts = others.reduce((acc, p) => { const v = attrValue(p, label); if (v) acc[String(v)] = (acc[String(v)] || 0) + 1; return acc; }, {});
      const values = Object.keys(counts).sort((a, b) => (parseFloat(a) - parseFloat(b)) || a.localeCompare(b));
      return { label, values, counts };
    }).filter(f => f.values.length >= 2 || attrFilter[f.label]?.length);
  })();
  const activeAttrCount = Object.values(attrFilter).filter(v => v.length).length;
  const toggleAttr = (label, value) => setAttrFilter(prev => {
    const cur = prev[label] || [];
    return { ...prev, [label]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
  });

  const toggleBrand = (brand) =>
    setBrandFilter(prev => (prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]));

  const applyPrice = () => setPriceFilter({
    min: priceInput.min === '' ? null : parseFloat(priceInput.min),
    max: priceInput.max === '' ? null : parseFloat(priceInput.max),
  });

  const resetFilters = () => {
    localStorage.removeItem(filtersKey);
    setBrandFilter([]);
    setPriceInput({ min: '', max: '' });
    setPriceFilter({ min: null, max: null });
    setAttrFilter({});
    setFuelFilter('');
    setActiveSub('');
  };

  const hasActiveFilters = brandFilter.length > 0 || priceFilter.min !== null || priceFilter.max !== null || !!activeSub || activeAttrCount > 0 || !!fuelFilter;


  if (isLegacyUrl && (!legacySous || categoryInfo)) {
    return <Navigate to={legacySous ? subcategoryPath(categoryInfo, legacySous) : `/pieces/${slug}`} replace />;
  }
  if (subSlug && categoryInfo && !activeSub) {
    // a level-3 node reached through a 2-segment URL (or an unknown slug) → canonical path / category
    const loose = !subsubSlug && subcategoryFromSlug(categoryInfo, subSlug);
    return <Navigate to={loose ? subcategoryPath(categoryInfo, loose) : `/pieces/${slug}`} replace />;
  }
  if (activeSub && categoryInfo && subcategoryPath(categoryInfo, activeSub) !== location.pathname) {
    return <Navigate to={subcategoryPath(categoryInfo, activeSub)} replace />;
  }
  const activeChildren = activeSub ? childrenOf(categoryInfo, activeSub) : [];
  const chipSubs = activeChildren.length ? activeChildren : (activeParent ? childrenOf(categoryInfo, activeParent) : topSubcategories(categoryInfo));
  const otherSubs = activeSub ? (activeParent ? childrenOf(categoryInfo, activeParent) : topSubcategories(categoryInfo)).filter(s => s !== activeSub) : [];

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Breadcrumb
          testId="category-breadcrumb"
          items={[
            { label: t('header.parts'), to: '/pieces' },
            activeSub ? { label: getCategoryName(), to: `/pieces/${slug}` } : { label: getCategoryName() },
            activeParent ? { label: subLabel(activeParent), to: subcategoryPath(categoryInfo, activeParent) } : null,
            activeSub ? { label: subLabel(activeSub) } : null,
          ]}
        />

        {/* Category header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="category-title">
            {activeSub ? (subSeo.h1 || <>{subLabel(activeSub)} <span className="text-gray-400 font-normal">› {activeParent ? subLabel(activeParent) : getCategoryName()}</span></>) : (seo.h1 || getCategoryName())}
          </h1>
          <p className="text-gray-500 mt-1" data-testid="category-product-count">{t('cat.found', { n: visibleProducts.length, count: visibleProducts.length })}</p>
          {!activeSub && seo.excerpt && <p className="text-gray-700 mt-2 leading-relaxed" data-testid="category-excerpt">{seo.excerpt}</p>}
          {activeSub && subSeo.excerpt && <p className="text-gray-700 mt-2 leading-relaxed" data-testid="subcategory-excerpt">{subSeo.excerpt}</p>}
          {chipSubs.length > 0 && (
            <>
              {activeChildren.length > 0 && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-4" data-testid="subsubcategories-label">{t('cat.subsubs', { name: subLabel(activeSub) })}</p>}
              <SubcategoryChips subcategories={chipSubs} labelOf={subLabel} images={categoryInfo.subcategory_images || {}} counts={countBySub} activeSub={activeChildren.length ? '' : activeSub} onSelect={(s) => setActiveSub(s || (activeChildren.length ? activeSub : activeParent))} />
            </>
          )}
        </div>

        {activeSub && activeChildren.length > 0 && !isLoading && <QuickCrossFilters category={categoryInfo} parent={activeSub} children={activeChildren} products={products} subsOf={subsOf} labelOf={subLabel} />}

        <VehicleBanner
          vehicle={vehicle}
          count={compatibleIds ? compatibleIds.size : undefined}
          showAll={showAll}
          onToggleShowAll={() => setShowAll(v => !v)}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Filters sidebar (slide-in panel on mobile) */}
            <FilterPanel resultCount={visibleProducts.length} onReset={resetFilters} activeCount={brandFilter.length + (priceFilter.min !== null || priceFilter.max !== null ? 1 : 0) + (activeSub ? 1 : 0) + activeAttrCount + (fuelFilter ? 1 : 0)}>
              <div className="bg-white rounded-lg shadow-sm p-4" data-testid="category-filters">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{t('cat.filterBy')}</h3>
                  {hasActiveFilters && (
                    <button data-testid="filters-reset" onClick={resetFilters} className="text-xs text-blue-600 hover:underline">
                      {t('cat.reset')}
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div data-testid="filter-fuel">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('fuelR.title')}</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {['', ...ENERGIES].map(e => (
                        <button key={e || 'all'} type="button" data-testid={`filter-fuel-${e || 'all'}`} aria-pressed={fuelFilter === e} onClick={() => setFuelFilter(e)}
                          className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${fuelFilter === e ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}>
                          {e ? energyLabel(t, e) : t('fuelR.all')}{e ? <span className={`ml-1 ${fuelFilter === e ? 'text-white/70' : 'text-gray-400'}`}>{fuelCounts[e]}</span> : null}
                        </button>
                      ))}
                    </div>
                    {vehicleEnergyKey && !fuelFilter && <p className="text-[11px] text-gray-400 mt-1" data-testid="filter-fuel-vehicle-hint">{t('fuelR.vehicleHint', { e: energyLabel(t, vehicleEnergyKey) })}</p>}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('cat.brand')}</h4>
                    {availableBrands.length === 0 && <p className="text-xs text-gray-400">{t('cat.noBrand')}</p>}
                    {availableBrands.map(brand => {
                      const count = baseProducts.filter(p => p.brand === brand).length;
                      return (
                        <label key={brand} className="flex items-center gap-2 py-1 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`filter-brand-${brand}`}
                            checked={brandFilter.includes(brand)}
                            onChange={() => toggleBrand(brand)}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-600 flex-1 flex items-center gap-2 min-w-0">
                            <BrandLogo brand={brand} size="xs" showName={false} testId={`filter-brand-logo-${brand}`} />
                            <span className="truncate">{brand}</span>
                          </span>
                          <span className="text-xs text-gray-400">{count}</span>
                        </label>
                      );
                    })}
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('cat.price')}</h4>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        placeholder="Min"
                        data-testid="filter-price-min"
                        value={priceInput.min}
                        onChange={(e) => setPriceInput(p => ({ ...p, min: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && applyPrice()}
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        type="number"
                        min="0"
                        placeholder="Max"
                        data-testid="filter-price-max"
                        value={priceInput.max}
                        onChange={(e) => setPriceInput(p => ({ ...p, max: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && applyPrice()}
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    </div>
                  </div>

                  <Button data-testid="filters-apply" onClick={applyPrice} className="w-full bg-orange-500 hover:bg-orange-600">{t('common.apply')}</Button>

                  {specFacets.length > 0 && (
                    <div className="pt-4 border-t border-gray-100" data-testid="filter-specs">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1.5"><SlidersHorizontal size={14} className="text-orange-500" /> {t('cat.specs')}</h4>
                      <p className="text-[11px] text-gray-400 mb-3">{t('cat.specsHint')}</p>
                      <div className="space-y-4">
                        {specFacets.map(f => {
                          const slug = f.label.replace(/[^\p{L}\p{N}]+/gu, '-');
                          const expanded = attrExpanded[f.label];
                          const shown = expanded ? f.values : f.values.slice(0, 6);
                          return (
                            <div key={f.label} data-testid={`filter-spec-${slug}`}>
                              <h5 className="text-xs font-medium text-gray-700 mb-1.5 flex items-center justify-between">
                                <span>{tt(f.label)}</span>
                                {attrFilter[f.label]?.length > 0 && (
                                  <button type="button" onClick={() => setAttrFilter(prev => ({ ...prev, [f.label]: [] }))} className="text-[11px] text-blue-600 hover:underline" data-testid={`filter-spec-clear-${slug}`}>{t('cat.reset')}</button>
                                )}
                              </h5>
                              {shown.map(v => (
                                <label key={v} className="flex items-center gap-2 py-0.5 cursor-pointer">
                                  <input type="checkbox" data-testid={`filter-spec-${slug}-${v.replace(/[^\p{L}\p{N}.]+/gu, '-')}`} checked={(attrFilter[f.label] || []).includes(v)} onChange={() => toggleAttr(f.label, v)}
                                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500" />
                                  <span className="text-sm text-gray-600 flex-1 truncate">{tt(v)}</span>
                                  <span className="text-xs text-gray-400">{f.counts[v] || 0}</span>
                                </label>
                              ))}
                              {f.values.length > 6 && (
                                <button type="button" onClick={() => setAttrExpanded(prev => ({ ...prev, [f.label]: !expanded }))} className="text-xs text-blue-600 hover:underline mt-1" data-testid={`filter-spec-more-${slug}`}>
                                  {expanded ? t('cat.showLess') : t('cat.showMore', { n: f.values.length - 6 })}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </FilterPanel>

            {/* Products grid */}
            <div className="lg:col-span-3">
              <SortSelect value={sort} onChange={setSort} count={visibleProducts.length} bestPrice={{ total: bestCount, enabled: bestOnly, onToggle: () => setBestOnly(v => !v) }} topRated={topRatedFilter.topRated} />
              {visibleProducts.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="category-empty">
                  <p className="text-gray-500">
                    {hasActiveFilters
                      ? t('cat.noneFilters')
                      : compatibleIds && !showAll
                        ? t('cat.noneVehicle')
                        : t('cat.none')}
                  </p>
                  {hasActiveFilters ? (
                    <Button variant="outline" className="mt-4" onClick={resetFilters} data-testid="filters-reset-empty">
                      {t('cat.resetFilters')}
                    </Button>
                  ) : compatibleIds && !showAll && (
                    <Button variant="outline" className="mt-4" onClick={() => setShowAll(true)}>
                      Voir toutes les pièces
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="category-products-grid">
                  {visibleProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      linkState={{ from: { category: slug, subcategory: activeSub || null } }}
                      compatible={compatibleIds ? compatibleIds.has(product.id) : false}
                      onAddToCart={handleAddToCart}
                      isAdding={addingToCart === product.id}
                      isNew={isRecentProduct(product)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {seo.description && (
          <section className="bg-white rounded-xl shadow-sm p-6 sm:p-10 mt-8" data-testid="category-description">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{seo.main_keyword ? t('cat.guideTitle', { kw: seo.main_keyword }) : t('cat.aboutTitle', { name: getCategoryName() })}</h2>
            <div className="prose-legal text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(seo.description) }} />
          </section>
        )}
        {categoryInfo && !activeSub && <FaqSection items={seo.faq} title={t('cat.faqTitle', { name: seo.main_keyword || getCategoryName() })} testId="category-faq" />}

        {otherSubs.length > 0 && (
          <section className="bg-white rounded-xl shadow-sm p-6 mt-8" data-testid="other-subcategories">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Layers size={18} className="text-orange-500" /> {t('cat.otherSubs', { name: activeParent ? subLabel(activeParent) : getCategoryName() })}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {otherSubs.map(s => (
                <Link key={s} to={subcategoryPath(categoryInfo, s)} data-testid={`other-subcategory-${s}`} className="group flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:border-orange-400 hover:shadow-sm transition-all">
                  <span className="w-12 h-12 rounded-md bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {categoryInfo.subcategory_images?.[s]
                      ? <img src={uploadsAPI.resolveUrl(categoryInfo.subcategory_images[s])} alt={s} className="w-full h-full object-cover no-placeholder" loading="lazy" />
                      : <img src={categoryInfo.image} alt="" className="w-8 h-8 object-contain opacity-40" />}
                  </span>
                  <span className="text-sm font-medium text-gray-800 group-hover:text-orange-600 leading-tight">{subLabel(s)}{countBySub[s] ? <span className="block text-xs font-normal text-gray-400">{t('cat.nProducts', { n: countBySub[s], count: countBySub[s] })}</span> : null}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {isFr && <TagLinks tags={categoryInfo?.tags} title={t('cat.related')} excludePath={activeSub ? subcategoryPath(categoryInfo || { slug }, activeSub) : `/pieces/${slug}`} testIdPrefix="category-tag" sectionTestId="category-tags" />}
        {categoryInfo && !isLoading && products.length > 0 && <PopularModelsBlock category={slug} sub={activeSub || null} />}
        {categoryInfo && !isLoading && products.length > 0 && <RelatedGuidesBlock category={slug} sub={activeSub || null} variant="category" />}
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;
